import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { Maximize2, X, Loader2 } from 'lucide-react';

/**
 * Shared caregiver setup-QR block (FECOA-1428/1429/1430/1431).
 *
 * Renders the setup QR for a single caregiver plus their Caregiver ID and
 * (when known) Temporary PIN, with an "Enlarge for Scanning" mode that takes
 * over the whole screen — hiding all other PII while the device faces the
 * caregiver (per the FECOA-1428 privacy note).
 *
 * The QR encodes a setup URL pointing at the caregiver login, sponsor-scoped
 * via the hash and pre-filling the Caregiver ID (and Temporary PIN when we
 * still hold it at creation time). When no PIN is available — e.g. retrieving
 * the QR later from the detail screen — only the ID is encoded and shown.
 */

/**
 * Build the sponsor-scoped setup URL the QR encodes. The caregiver login reads
 * `sponsor` from the hash and pre-fills `cg` / `pin` from the query string.
 */
export const buildCaregiverSetupUrl = ({ caregiverId, tempPin, sponsorId }) => {
  const origin =
    typeof window !== 'undefined' && window.location ? window.location.origin : '';
  const params = new URLSearchParams();
  if (caregiverId) params.set('cg', caregiverId);
  if (tempPin) params.set('pin', tempPin);
  const query = params.toString();
  const hash = sponsorId ? `#${sponsorId}` : '';
  return `${origin}/caregiver-login${query ? `?${query}` : ''}${hash}`;
};

const useQrDataUrl = (text, width) => {
  const [dataUrl, setDataUrl] = useState('');
  useEffect(() => {
    let cancelled = false;
    if (!text) {
      setDataUrl('');
      return undefined;
    }
    QRCode.toDataURL(text, { width, margin: 1, errorCorrectionLevel: 'M' })
      .then((url) => {
        if (!cancelled) setDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setDataUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, [text, width]);
  return dataUrl;
};

const CaregiverSetupQR = ({ caregiverId, tempPin, sponsorId, relationship, name }) => {
  const [enlarged, setEnlarged] = useState(false);
  const setupUrl = buildCaregiverSetupUrl({ caregiverId, tempPin, sponsorId });
  const qrUrl = useQrDataUrl(setupUrl, 320);
  const qrUrlLarge = useQrDataUrl(enlarged ? setupUrl : '', 480);

  return (
    <div className="flex flex-col items-center">
      {name && (
        <div className="text-center mb-3">
          <p className="text-base font-semibold text-gray-900 dark:text-white">{name}</p>
          {relationship && (
            <p className="text-sm text-gray-500 dark:text-gray-400">{relationship}</p>
          )}
        </div>
      )}

      <div className="bg-white rounded-lg p-4 shadow-sm">
        {qrUrl ? (
          <img
            src={qrUrl}
            alt={`Setup QR code for ${caregiverId}`}
            className="w-48 h-48"
          />
        ) : (
          <div className="w-48 h-48 flex items-center justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        )}
      </div>

      <div className="mt-4 text-center">
        <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Caregiver ID</p>
        <p className="text-lg font-mono font-semibold text-primary-600 dark:text-primary-400">
          {caregiverId}
        </p>
      </div>

      {tempPin && (
        <div className="mt-2 text-center">
          <p className="text-xs uppercase text-gray-500 dark:text-gray-400">Temporary PIN</p>
          <p className="text-lg font-mono font-semibold tracking-widest text-primary-600 dark:text-primary-400">
            {tempPin}
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={() => setEnlarged(true)}
        className="mt-4 w-full inline-flex items-center justify-center px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-md text-sm font-medium"
      >
        <Maximize2 className="w-4 h-4 mr-2" />
        Enlarge for Scanning
      </button>

      {/* Fullscreen scan mode — hides all other PII while the device faces the
          caregiver (FECOA-1428 privacy note). Only the QR + this caregiver's
          own ID/PIN are visible. */}
      {enlarged && (
        <div className="fixed inset-0 z-[60] bg-slate-900 flex flex-col items-center justify-center px-4">
          <button
            type="button"
            onClick={() => setEnlarged(false)}
            aria-label="Close scan view"
            className="absolute top-4 right-4 w-9 h-9 flex items-center justify-center rounded-full bg-slate-700 hover:bg-slate-600 text-white"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="bg-white rounded-xl p-5 shadow-lg">
            {qrUrlLarge ? (
              <img
                src={qrUrlLarge}
                alt={`Setup QR code for ${caregiverId}`}
                className="w-64 h-64 sm:w-72 sm:h-72"
              />
            ) : (
              <div className="w-64 h-64 sm:w-72 sm:h-72 flex items-center justify-center">
                <Loader2 className="w-7 h-7 animate-spin text-gray-400" />
              </div>
            )}
          </div>
          <div className="mt-5 text-center">
            <p className="text-xs uppercase text-gray-400">Caregiver ID</p>
            <p className="text-xl font-mono font-semibold text-primary-400">{caregiverId}</p>
          </div>
          {tempPin && (
            <div className="mt-3 text-center">
              <p className="text-xs uppercase text-gray-400">Temporary PIN</p>
              <p className="text-xl font-mono font-semibold tracking-widest text-primary-400">
                {tempPin}
              </p>
            </div>
          )}
          <p className="mt-4 text-xs text-gray-400 text-center max-w-xs">
            Scan the QR code or enter the credentials above to complete setup.
          </p>
        </div>
      )}
    </div>
  );
};

export default CaregiverSetupQR;
